import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../constants';

// Helper function to get token from localStorage
const getStoredToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('authToken');
  }
  return null;
};

// Helper function to get stored user data
const getStoredUser = () => {
  if (typeof window !== 'undefined') {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }
  return null;
};

// Initial state with persistence check
const initialState = {
  user: getStoredUser(),
  role: getStoredUser()?.role || null,
  isLoggedIn: !!getStoredToken(),
  loading: false,
  error: null,
};

// Async thunk to fetch user info from API
export const getUserInfo = createAsyncThunk(
  'auth/getUserInfo',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      let token = state.auth.user?.token;
      
      // If no token in Redux, fall back to localStorage
      if (!token) {
        token = localStorage.getItem('authToken');
        if (!token) throw new Error('No token found. Please log in.');
      }

      const response = await fetch(`${API_BASE_URL}/api/web/staff/auth-user`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch user info');

      const data = await response.json();
      const userData = data?.user;

      return userData;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch user info');
    }
  }
);

// Slice definition
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action) => {
      state.user = action.payload.user;
      state.role = action.payload.role;
      state.isLoggedIn = true;
      state.error = null;
      
      // Store in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('authToken', action.payload.user.token);
        localStorage.setItem('userData', JSON.stringify(action.payload.user));
      }
    },
    updateStatus: (state, action) => {
      if (state.user) {
        state.user.status = action.payload;
      }
    },
    logout: (state) => {
      state.user = null;
      state.role = null;
      state.isLoggedIn = false;
      state.error = null;
      
      // Clear localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isLoggedIn = true;
        state.error = null;
        
        // Update localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('userData', JSON.stringify(action.payload));
        }
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Don't logout on error, just show the error
      });
  },
});

export const { login, updateStatus, logout, clearError } = authSlice.actions;
export default authSlice.reducer;
// export const selectUser = (state) => state.auth.user;
// export const selectIsLoggedIn = (state) => state.auth.isLoggedIn;